import React, { useEffect, useContext } from 'react'
import {
    Box,
    Flex,
    Heading,
    HStack,
    Text,
    useColorMode,
    VStack,
    Wrap
} from '@chakra-ui/react'
import Section from '../components/Section'
import {
    programming_languages,
    backend_systems,
    frontend,
    databases,
    cloud_devops,
    monitoring,
    developer_tools,
    soft_skills,
    artificial_intelligence
} from "../data/skills"

import SkillButton from "../components/Skills/SkillButton"
import { motion, AnimatePresence } from 'framer-motion'
import { FaRocket } from 'react-icons/fa'
import { FiActivity, FiCode, FiCpu, FiDatabase, FiLayers, FiTool } from 'react-icons/fi'
import { MdCloudQueue, MdPsychology } from 'react-icons/md'
import Loader from '../components/Loader/Loader'
import loaderContext from '../context/loaderContext'
import SEOHead from "../components/SEOHead"
import { getTokens } from '../config/colorTokens'
import { pageVariants, containerVariants, itemVariants, heroVariants } from '../config/animations'

const MotionVStack = motion(VStack)
const MotionBox = motion(Box)

const Skills = () => {
    const { colorMode } = useColorMode()
    const context = useContext(loaderContext)
    const { loading, setLoading } = context
    const t = getTokens(colorMode)

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 1200)
        return () => clearTimeout(timer)
    }, [setLoading])

    const skillSections = [
        {
            title: "Programming Languages",
            description: "Daily drivers for application services, scripting, and data work.",
            data: programming_languages,
            icon: FiCode
        },
        {
            title: "Backend & Distributed Systems",
            description: "Service design, APIs, event streaming, and production-grade backends.",
            data: backend_systems,
            icon: FiCpu
        },
        {
            title: "Frontend & UI",
            description: "Responsive interfaces with modern React component systems.",
            data: frontend,
            icon: FiLayers
        },
        {
            title: "Databases",
            description: "Relational, document, cache, and realtime data stores.",
            data: databases,
            icon: FiDatabase
        },
        {
            title: "Cloud & DevOps",
            description: "Containerized deployments and cloud-native delivery workflows.",
            data: cloud_devops,
            icon: MdCloudQueue
        },
        {
            title: "Monitoring & Observability",
            description: "Dashboards and operational visibility for healthier systems.",
            data: monitoring,
            icon: FiActivity
        },
        {
            title: "Artificial Intelligence",
            description: "Agentic systems, RAG patterns, and practical generative AI workflows.",
            data: artificial_intelligence,
            icon: MdPsychology
        },
        {
            title: "Developer Tools",
            description: "Version control, API tooling, IDEs, and platform utilities.",
            data: developer_tools,
            icon: FiTool
        },
        {
            title: "Soft Skills",
            description: "Clear thinking and communication across engineering teams.",
            data: soft_skills,
            icon: FaRocket
        }
    ]
    return (
        <div>
            <SEOHead 
                title="Skills | Prasoon Soni"
                description="Technical expertise of Prasoon Soni in backend systems, distributed architecture, frontend development, cloud, and modern engineering tools."
                path="/skills"
            />

            <AnimatePresence>
                {loading ? (
                    <Loader key="loader" />
                ) : (
                    <MotionVStack
                        key="content"
                        variants={pageVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        spacing={0}
                        w="100%"
                        minH="100vh"
                    >
                        <Section id="skills">
                            <VStack align="stretch" spacing={7} p={4}>

                                {/* Hero */}
                                <MotionBox variants={heroVariants}>
                                    <VStack align="start" spacing={4}>
                                        <Heading>Skills</Heading>
                                        <Text
                                            fontSize={{ base: "md", md: "lg" }}
                                            color={t.textSubtle}
                                            fontWeight="light"
                                            maxW="2xl"
                                        >
                                            Building scalable systems with a strong focus on backend engineering, distributed systems, and modern web technologies.
                                        </Text>
                                        <Box
                                            h="2px"
                                            w="60px"
                                            bgGradient={t.accentGradient}
                                            borderRadius="full"
                                        />
                                    </VStack>
                                </MotionBox>

                                {/* Sections */}
                                <MotionVStack
                                    align="stretch"
                                    spacing={4}
                                    variants={containerVariants}
                                >
                                    {skillSections.map((section, idx) => (
                                        <MotionBox
                                            key={idx}
                                            variants={itemVariants}
                                            whileHover={{
                                                y: -3,
                                                transition: { type: 'spring', stiffness: 260, damping: 18 }
                                            }}
                                        >
                                            <VStack
                                                align="stretch"
                                                border="1px"
                                                borderRadius="8px"
                                                backdropFilter="blur(6px)"
                                                borderColor={t.borderCard}
                                                bg="transparent"
                                                p={{ base: 4, md: 5 }}
                                                spacing={4}
                                            >
                                                <Flex align="start" gap={4}>
                                                    <HStack align="start" spacing={3}>
                                                        <Flex
                                                            align="center"
                                                            justify="center"
                                                            boxSize="36px"
                                                            borderRadius="8px"
                                                            bg={t.hoverBg}
                                                            color={t.iconDefault}
                                                            flexShrink={0}
                                                            mt="2px"
                                                        >
                                                            <Box as={section.icon} size="18px" />
                                                        </Flex>
                                                        <Box>
                                                            <Text
                                                                color={t.textPrimary}
                                                                fontWeight="semibold"
                                                                fontSize={{ base: "md", md: "lg" }}
                                                            >
                                                                {section.title}
                                                            </Text>
                                                            <Text
                                                                color={t.textSubtle}
                                                                fontSize="sm"
                                                                mt={1}
                                                            >
                                                                {section.description}
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                </Flex>

                                                <Wrap spacing={2} paddingTop={1}>
                                                    {section.data.map((el) => (
                                                        <SkillButton
                                                            key={el.id}
                                                            text={el.text}
                                                            icon={el.icon}
                                                            color={el.color}
                                                        />
                                                    ))}
                                                </Wrap>
                                            </VStack>
                                        </MotionBox>
                                    ))}
                                </MotionVStack>

                            </VStack>
                        </Section>
                    </MotionVStack>
                )}
            </AnimatePresence>
        </div>
    )
}

export default Skills
